import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ImportFiles from "../../components/importFiles/ImportFiles";
import { useTranslator } from "../../context/translator";
import {
  getContractFileByIdService,
  updateContractFileService,
} from "../../services/contractFile/services";
import { useSelector } from "react-redux";
import Loading from "../loading/Loading";
import Pagination from "../pagination/Pagination";
import NoResults from "../noResults/noResults";
import { downloadFileToAzure } from "../../utils/azure/azure";
import ConfirmDeleteModal from "../confirmDeleteModal/ConfirmDeleteModal";
import { useNotifications } from "../../context/Notifications";
import { useGetCompanyQuery, useGetUserByIdQuery } from "store/api/apiSlice";

const ContractList = () => {
  const { translate } = useTranslator();
  const { clientId, contractId } = useParams();
  const { user } = useSelector((state) => state.user);
  const { data: userProfile, isLoading: userLoading } = useGetUserByIdQuery(
    user.id,
    {
      skip: !user?.id,
      pollingInterval: 60000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const { data: company, isLoading: companyLoading } = useGetCompanyQuery(
    userProfile?.user?.companyId,
    {
      skip: !userProfile?.user?.companyId,
    },
  );
  const { addNotification } = useNotifications();

  const [importModal, setImportModal] = useState(false);
  const [page, setPage] = useState(1);
  const [pageNumber, setPageNumber] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [totalContractFiles, setTotalContractFiles] = useState(0);
  const [contractFilesData, setContractFilesData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const toggleImportFiles = () => setImportModal((prev) => !prev);

  const getFiles = useCallback(async () => {
    setIsLoading(true);
    const data = await getContractFileByIdService({
      contractId,
      page,
      search: "all",
      pageSize: 5,
    });
    setIsLoading(false);

    setContractFilesData(data?.contractFiles ?? []);
    const count = data?.totalContractFiles ?? 0;
    setTotalContractFiles(count);
    setPageNumber(Math.ceil(count / 5));
  }, [contractId, page]);

  useEffect(() => {
    getFiles();
  }, [getFiles]);

  const viewFile = async (file) => {
    if (!file?.linkUrl) {
      console.error("File URL is missing");
      return;
    }

    // Create a temporary <a> element to trigger download
    const link = document.createElement("a");
    link.href = await downloadFileToAzure(file);
    link.download = file.name || "download";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadFile = async (file) => {
    if (!file?.linkUrl) {
      console.error("File URL is missing");
      return;
    }

    try {
      const downLink = await downloadFileToAzure(file);
      const response = await fetch(downLink);

      if (!response.ok) {
        throw new Error("Failed to fetch file");
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = file.name || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const handleDelete = (fileName) => {
    setSelectedFile(fileName);
    setIsModalOpen(true);
  };

  const confirmDelete = () => {
    deleteFile(selectedFile);
  };

  const deleteFile = async (file) => {
    const response = await updateContractFileService({
      user: userProfile?.user,
      contractFileId: file?._id,
      data: {
        active: false,
        companyId: company?._id,
        customerId: clientId,
        contractId,
        name: file.name,
      },
    });

    if (response?.contractFile?.contractFiles?.acknowledged) {
      addNotification({
        id: Date.now(),
        message: translate("file-deleted-successfully"),
        kind: "success",
      });
      setIsModalOpen(false);
      setSelectedFile(null);
      await getFiles();
    } else {
      addNotification({
        id: Date.now(),
        message: translate("error-deleting-file"),
        kind: "danger",
      });
    }
  };

  if (userLoading || companyLoading) return null;

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
          {translate("contract-files")}
        </h1>
        <button
          type="button"
          className="text-white bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-blue-300 dark:focus:ring-blue-800 font-medium rounded-lg text-sm px-5 py-2.5"
          onClick={toggleImportFiles}
        >
          {translate("import")}
        </button>
      </div>

      <ImportFiles
        key="importFilesModal"
        modalOpen={importModal}
        toggleModal={toggleImportFiles}
        loadFiles={getFiles}
        clientId={clientId}
        contractId={contractId}
      />

      <div>
        {isLoading && <Loading />}
        {!isLoading && contractFilesData.length === 0 && (
          <NoResults message={translate("no-files-found")} />
        )}
        {!isLoading && contractFilesData.length > 0 && (
          <>
            <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  <th className="px-6 py-3">{translate("name")}</th>
                  <th className="px-6 py-3">{translate("type")}</th>
                  <th className="px-6 py-3 text-right">
                    {translate("actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {contractFilesData.map((file) => (
                  <tr
                    key={file?._id}
                    className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    <td className="px-6 py-4 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                      {file.name}
                    </td>
                    <td className="px-6 py-4">{file.type}</td>
                    <td className="px-6 py-4 text-right">
                      <button
                        type="button"
                        className="font-medium text-blue-600 dark:text-blue-500 hover:underline mr-4"
                        onClick={() => downloadFile(file)}
                      >
                        {translate("download")}
                      </button>
                      <button
                        type="button"
                        className="font-medium text-blue-600 dark:text-blue-500 hover:underline mr-4"
                        onClick={() => viewFile(file)}
                      >
                        {translate("view")}
                      </button>
                      <button
                        type="button"
                        className="font-medium text-red-600 dark:text-red-500 hover:underline"
                        onClick={() => handleDelete(file)}
                      >
                        {translate("delete")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <Pagination
              key="paginationContractFile"
              data={contractFilesData}
              page={page}
              pageNumber={pageNumber}
              isLoading={isLoading}
              setPage={setPage}
              totalRecords={totalContractFiles}
            />
          </>
        )}
      </div>
      <ConfirmDeleteModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={confirmDelete}
        fileName={selectedFile?.name || ""}
      />
    </div>
  );
};

export default ContractList;
